import React, { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';

const RenderLatex = ({ children, content, onImageRequest }) => {
  const ref = useRef(null);

  // Keep a stable ref so DOM click-handlers never capture a stale closure
  const onImageRequestRef = useRef(onImageRequest);
  useEffect(() => {
    onImageRequestRef.current = onImageRequest;
  });

  const rawContent = children || content;

  /** Inject "Add image" buttons next to every section heading. */
  const injectImageButtons = (container) => {
    if (!container || !onImageRequestRef.current) return;

    container.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(heading => {
      if (heading.querySelector('.section-img-btn')) return; // already added

      const sectionName = heading.textContent.replace(/\s*Add image\s*$/, '').trim();
      if (!sectionName) return;

      heading.style.display = 'flex';
      heading.style.alignItems = 'center';
      heading.style.justifyContent = 'space-between';
      heading.style.gap = '8px';

      const btn = document.createElement('button');
      btn.className = 'section-img-btn';
      btn.title = 'Click to add an AI-generated image to this section';
      btn.setAttribute('data-section', sectionName);
      btn.style.cssText = [
        'background:none', 'border:1px solid transparent', 'cursor:pointer',
        'padding:3px 7px', 'border-radius:6px', 'display:inline-flex',
        'align-items:center', 'gap:4px', 'color:#94a3b8', 'font-size:11px',
        'font-family:inherit', 'font-weight:500', 'flex-shrink:0',
        'transition:color 0.15s,background 0.15s,border-color 0.15s',
        'white-space:nowrap', 'line-height:1',
      ].join(';');

      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
        </svg>
        <span>Add image</span>
      `;

      btn.addEventListener('mouseenter', () => {
        btn.style.color = '#6366f1';
        btn.style.background = '#eef2ff';
        btn.style.borderColor = '#c7d2fe';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.color = '#94a3b8';
        btn.style.background = 'none';
        btn.style.borderColor = 'transparent';
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const section = btn.getAttribute('data-section');
        if (onImageRequestRef.current) onImageRequestRef.current(section);
      });

      heading.appendChild(btn);
    });
  };

  /**
   * Wire delete-button and lightbox-click handlers on injected image containers.
   * Uses custom events so content-generation.js can listen without prop-threading.
   */
  const wireImageContainers = (container) => {
    container.querySelectorAll('.gen-img-container').forEach(wrap => {
      const imgId = wrap.getAttribute('data-img-id');

      // Delete button
      const delBtn = wrap.querySelector('[data-action="delete-image"]');
      if (delBtn && !delBtn.dataset.wired) {
        delBtn.dataset.wired = '1';
        delBtn.addEventListener('mouseenter', () => {
          delBtn.style.transform = 'scale(1.15)';
        });
        delBtn.addEventListener('mouseleave', () => {
          delBtn.style.transform = 'scale(1)';
        });
        delBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          document.dispatchEvent(new CustomEvent('section-image-delete', {
            detail: { imgId }
          }));
        });
      }

      // Image lightbox
      const img = wrap.querySelector('img[data-action="lightbox"]');
      if (img && !img.dataset.wired) {
        img.dataset.wired = '1';
        img.addEventListener('click', () => {
          document.dispatchEvent(new CustomEvent('section-image-lightbox', {
            detail: { src: img.getAttribute('data-src') || img.src }
          }));
        });
      }
    });
  };

  useEffect(() => {
    if (!ref.current || !rawContent) return;

    let html = String(rawContent);
    html = html.replace(/^```(?:html)?\s*\n?/i, '').replace(/\n?```\s*$/i, '');

    const element = ref.current;
    element.innerHTML = '';

    const hasLatex = /\$[^$]+\$/.test(html);

    if (!hasLatex) {
      element.innerHTML = DOMPurify.sanitize(html, {
        ADD_TAGS: ['h1','h2','h3','h4','h5','h6','p','b','i','ul','ol','li','br','strong',
                   'em','table','thead','tbody','tr','th','td','span','div','a',
                   'blockquote','code','pre','img'],
        ADD_ATTR: ['style','href','target','class','src','alt','data-action',
                   'data-img-id','data-src','data-wired'],
      });
      injectImageButtons(element);
      wireImageContainers(element);
      return;
    }

    // Has LaTeX — parse character by character
    let text = '';
    let math = '';
    let inMath = false;

    for (let i = 0; i < html.length; i++) {
      const ch = html[i];
      if (ch === '$' && !inMath) {
        if (text) {
          const span = document.createElement('span');
          span.innerHTML = DOMPurify.sanitize(text);
          element.appendChild(span);
          text = '';
        }
        inMath = true;
        if (html[i + 1] === '$') i++;
      } else if (ch === '$' && inMath) {
        if (html[i + 1] === '$') i++;
        try {
          const rendered = katex.renderToString(math, { throwOnError: false, displayMode: math.startsWith('$') });
          element.insertAdjacentHTML('beforeend', rendered);
        } catch (e) {
          element.appendChild(document.createTextNode('$' + math + '$'));
        }
        math = '';
        inMath = false;
      } else if (inMath) {
        math += ch;
      } else {
        text += ch;
      }
    }

    if (text) {
      const span = document.createElement('span');
      span.innerHTML = DOMPurify.sanitize(text);
      element.appendChild(span);
    }
    if (math) element.appendChild(document.createTextNode('$' + math + '$'));

    injectImageButtons(element);
    wireImageContainers(element);

  }, [rawContent]);

  return <div ref={ref} />;
};

export default RenderLatex;